import { useCallback } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useZervantSettings } from '../../invoicingSettings/hooks/useZervantSettings'
import { defaultVatRatesPredefinedTags } from '../constants/defaultVatRatePredefinedTags'
import { useVatRates } from '../contexts/vatRatesContext'
import { VatRate } from '../types/vatRate'
import { findVatRateById } from '../utils/findVatRateById'
import { getVatRatesSortedByPriorities } from '../utils/getVatRatesSortedByPriorities'

interface GetVatRateOptions {
  vatRateId?: string
  vatRates?: VatRate[]
}

export const useGetVatRate = () => {
  const { organization } = useUserOrganization()
  const { vatRates: defaultVatRates, isLoading: isLoadingVatRates } = useVatRates()
  const { data: zervantSettings, isLoading: isLoadingZervantSettings } = useZervantSettings()

  // tax rate id and the predefinedTag are now the same
  const companyDefaultTaxRateId = zervantSettings?.values?.company?.vatRateId

  const isLoading = isLoadingZervantSettings || isLoadingVatRates

  // We're providing a vatRates set of data which can (but doesn't have to)
  // be filtered comparing to what we get from the backend. And with that:
  // 1. Get by specific VatRate ID, if provided - if found, return it
  // 2. Get by the default id from the settings - if found, return it
  // 3. Get by default predefined tag for an org country (if exists)
  //    - if found, return it
  // 4. Get the first one from the list and return it
  const getVatRate = useCallback(
    ({ vatRateId, vatRates }: GetVatRateOptions) => {
      if (!vatRates && isLoading) {
        return undefined
      }

      // prepare the vat rate list - provided or fallback to the default one
      const vatRatesData = vatRates || defaultVatRates

      // for vat exempt, pick any 0% vat rate available
      if (organization?.isVatExempted) {
        const zeroVatRate = vatRatesData.find((rate) => rate.rate === 0)

        if (zeroVatRate) {
          return zeroVatRate
        }
      }

      // try to get vat rate by id
      const vatRateById = findVatRateById(vatRatesData, vatRateId)

      if (vatRateById) {
        return vatRateById
      }

      // try to get the vat rate by the default tax rate id (that comes from Zervant settings service)
      const vatRateByDefaultTaxRateId = findVatRateById(vatRatesData, companyDefaultTaxRateId)

      if (vatRateByDefaultTaxRateId) {
        return vatRateByDefaultTaxRateId
      }

      // try to fallback to a predefined tag for a specific country
      const defaultPredefinedTag = defaultVatRatesPredefinedTags[organization?.countryId || '']
      const vatRateByDefaultPredefinedTag = findVatRateById(vatRatesData, defaultPredefinedTag)

      if (vatRateByDefaultPredefinedTag) {
        return vatRateByDefaultPredefinedTag
      }

      // fallback to first one from the list
      const vatRatesSortedByPriorities = getVatRatesSortedByPriorities(vatRatesData)

      return vatRatesSortedByPriorities?.[0]
    },
    [companyDefaultTaxRateId, defaultVatRates, isLoading, organization?.countryId, organization?.isVatExempted],
  )

  return { getVatRate, isLoading }
}
